import React, { useCallback, useRef, useState } from 'react'
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { createMedia,  deleteMedia, fetchMedias } from '../../api/media';
import { toast } from 'react-toastify';
import IsEmpty from '../../components/IsEmpty';



const MAX_FILE_SIZE_MB = 2; // Taille maximale de fichier en Mo

const MediaUploader = () => {
    const queryClient = useQueryClient();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [errors, setErrors] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);



    // supression d'une image de la liste
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, imageIndex: null });
    const handleContextMenu = (event, index) => {
        event.preventDefault();
        setContextMenu({
            visible: true,
            x: event.clientX,
            y: event.clientY,
            imageIndex: index,
        });
    };
    const handleDeleteImage = () => {
        const newImageSet = selectedFiles.filter((_, index) => index !== contextMenu.imageIndex);
        setSelectedFiles(newImageSet);
        setContextMenu({ visible: false, x: 0, y: 0, imageIndex: null });
    };
    const handleClickOutside = () => {
        setContextMenu({ visible: false, x: 0, y: 0, imageIndex: null });
    };


    // Mutation pour téléverser une seule image
    const uploadImageMutation = useMutation({
        mutationFn: (file) => createMedia(file),
        onSuccess: () => {
            queryClient.invalidateQueries(['medias']);
        },
        onError: (error, file) => {
            setErrors((prev) => [...prev, `Erreur sur le fichier ${file.name}`]);
        },
    });

    // Gestion de la sélection des fichiers
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const oversizedFiles = files.filter(
            (file) => file.size > MAX_FILE_SIZE_MB * 1024 * 1024
        );

        if (oversizedFiles.length > 0) {
            setErrors((prev) => [
                ...prev,
                `Certains fichiers dépassent la taille limite de ${MAX_FILE_SIZE_MB} Mo.`,
            ]);
            return;
        }

        const uniqueFiles = files.filter(
            (file) => !selectedFiles.some((f) => f.name === file.name)
        );
    
        if (uniqueFiles.length < files.length) {
            setErrors((prev) => [
                ...prev,
                `Certains fichiers sont déjà sélectionnés.`,
            ]);
        }

        setErrors([]); // Réinitialise les erreurs
        setSelectedFiles((prev) => [...prev, ...uniqueFiles]);
    };

    // Gérer le téléversement des images une par une
    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setErrors(['Aucun fichier sélectionné.']);
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setErrors([]);

        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];

            try {
                // Téléverse le fichier via la mutation
                await uploadImageMutation.mutateAsync(file);

                // Met à jour la progression
                setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
            } catch (error) {
                setErrors((prev) => [
                    ...prev,
                    error?.message ? `Echec: ${error?.message}` : `Échec du téléversement pour le fichier ${file.name}.`,
                ]);
            }
        }

        setIsUploading(false);
        setSelectedFiles([]);
    };

    return (
        <div className='p-2' onClick={handleClickOutside}>
            <h1>Uploader des images</h1>

            {/* Champ pour sélectionner plusieurs fichiers */}
            <form encType="multipart/form-data">
                <div className='inputFile mb-2'>
                    <label htmlFor='image' className='d-flex flex-column justify-content-center align-items-center h-100 p-2'>
                        <i className="fa-light fa-images fs-3 opacity-75"></i>
                        <span className='text-center fw-medium'>Cliquer pour choisir des images ( Entre 0 - 1Mo )</span>
                    </label>
                    <input type="file" multiple onChange={handleFileChange} id='image' accept='image/*' className='w-100 d-none'/>
                </div>
                {errors.length > 0 && (
                    <div style={{ color: 'red' }}>
                        {errors.map((err, index) => (
                            <p key={index} className='text-danger fs-9s mb-1'>{err}</p>
                        ))}
                    </div>
                )}
            </form>

            {/* Liste des fichiers sélectionnés */}
            {selectedFiles?.length > 0 &&(
                <div className='bg-black p-2 rounded-3 shadow d-flex justify-content-start flex-wrap g-1 mt-4 overflow-y-scroll' style={{maxHeight:"70vh"}}>
                    {selectedFiles?.map((image, index) => (
                        <img
                            key={index}
                            src={URL.createObjectURL(image)}
                            alt=''
                            className='img-fluid object-fit-contain m-1 border bordercolor-primary'
                            style={{ height: "5rem", width: "auto" }}
                            onContextMenu={(event) => handleContextMenu(event, index)}
                        />
                    ))}
                </div>
            )}

            {/* Barre de progression */}
            {isUploading && (
                <div className="progress mt-2" role="progressbar" aria-label="Animated striped example">
                    <div className="progress-bar bg-success progress-bar-striped progress-bar-animated fs-9s" style={{width: uploadProgress+"%"}}>Téléversement en cours : {uploadProgress}%</div>
                </div>
            )}

            {/* Bouton pour lancer le téléversement */}
            <button
                onClick={handleUpload}
                disabled={isUploading}
                className='form-control w-auto rounded-0 fs-9s py-1 px-3 mt-2 submit'
            >
                {isUploading ? (
                    <>
                        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                        <span role="status">Téléversement...</span>
                    </>
                ) : 'Téléverser'}
            </button>


            {/* tooltip pour Supprimer une image */}
            {contextMenu.visible && (
                <div
                    className='context-menu box-shadow p-1 rounded-2'
                    style={{
                        position: 'absolute',
                        top: `${contextMenu.y}px`,
                        left: `${contextMenu.x}px`,
                        backgroundColor: 'rgb(109, 109, 109)',
                        border: '1px solid rgb(224, 202, 169)',
                        zIndex: 1000,
                    }}
                >
                    <button onClick={handleDeleteImage} className='btn shadow-none text-white'>Supprimer</button>
                </div>
            )}
        </div>
    );
};




const Medias = () => {

    const queryClient = useQueryClient();


    // **1. Fetch All Medias**
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status,} = useInfiniteQuery({
        queryKey: ["medias"],
        queryFn: fetchMedias,
        getNextPageParam: (lastPage) => lastPage.nextPage,
    });



    const observer = useRef();
    const lastMediaRef = useCallback((node) => {
        if (isFetchingNextPage) return;
        if (!hasNextPage) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                fetchNextPage(); 
            }
        });

        if (node) observer.current.observe(node);
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);
    

    // **2. Delete Media Mutation**
    const toastAdd = useRef(null);
    const deleteMediaMutation = useMutation({
        mutationFn: (mediaId) => deleteMedia(mediaId),
        onMutate: () => {
            toastAdd.current = toast.loading("Suppression en cours...", { 
                position: 'top-right',
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['medias']); 
            toast.dismiss(toastAdd.current)
            toastAdd.current = toast.success("Image supprimée avec succès", {
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true,
            });
        },
        onError: (error) => {
            console.error('Error deleting media:', error);
            toast.dismiss(toastAdd.current)
            toastAdd.current = toast.error("Une erreur est survenue: "+error.message, {
                isLoading: false,
                hideProgressBar: false,
                closeOnClick: true,
            });
        },
    });


     // **Handle Media Deletion**
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, imageIndex: null });
    const handleContextMenu = (event, index) => {
        event.preventDefault();
        setContextMenu({
            visible: true,
            x: event.clientX,
            y: event.clientY,
            imageIndex: index,
        });
    };
    const handleDeleteMedia = () => {
        const id = contextMenu.imageIndex
        if (!id) return;
        
        deleteMediaMutation.mutate(id);
        setContextMenu({ visible: false, x: 0, y: 0, imageIndex: null });
    };
    const handleClickOutside = () => {
        setContextMenu({ visible: false, x: 0, y: 0, imageIndex: null });
    };

    return (
        <div onClick={handleClickOutside}>
            <h1 className='fs-3 fw-bold mb-2'>Médias</h1>

            <MediaUploader/>

            <h2 className='fw-bold mt-4'>Parcourir</h2>
            <div className='bg-black p-2 rounded-3 shadow d-flex justify-content-start flex-wrap g-1 mt-2 overflow-y-scroll' style={{maxHeight:"70vh"}}>
                {
                    status === "pending" ? (
                        <div className=' d-flex justify-content-center align-items-center h-100 w-100'>
                            <p className="fs-9s">Chargement des médias...</p>
                        </div>
                    ) : status === "error" ? (
                        <div className=' d-flex justify-content-center align-items-center h-100 w-100'>
                            <p className="fs-9s">Une erreur est survenue.</p>
                        </div>
                    )
                    :data?.pages?.flatMap((page, pageIndex) => 
                        page.data.map((media, index) => {
                            const isLastItem = pageIndex === data.pages.length - 1 && index === page.data.length - 1;
                            return (
                                <img
                                    key={media.id}
                                    ref={isLastItem ? lastMediaRef : null}
                                    src={media.filePath}
                                    alt=''
                                    className='img-fluid object-fit-contain m-1 border bordercolor-primary'
                                    style={{ height: "5rem", width: "auto" }}
                                    onContextMenu={(event) => handleContextMenu(event, media.id)}
                                />
                            )
                        })
                    )
                }
                {(data?.pages?.length < 0) && <IsEmpty icon={'fa-light fa-image'} message={'Aucune image'} />}
                {isFetchingNextPage && <div className=' d-flex justify-content-center align-items-center h-100 w-100'><p className="fs-9s">Chargement...</p></div>}
            </div>



            {contextMenu.visible && (
                <div
                    className='context-menu box-shadow p-1 rounded-2'
                    style={{
                        position: 'absolute',
                        top: `${contextMenu.y}px`,
                        left: `${contextMenu.x}px`,
                        backgroundColor: 'rgb(109, 109, 109)',
                        border: '1px solid rgb(224, 202, 169)',
                        zIndex: 1000,
                    }}
                >
                    <button onClick={handleDeleteMedia} className='btn shadow-none text-white'>Supprimer</button>
                </div>
            )}

        </div>
    )
}

export default Medias